import React, { useEffect, useState } from "react";
import ToolShell from "@/components/ToolShell";
import CurrencyInput from "@/components/CurrencyInput";
import CollegeSelect from "@/components/CollegeSelect";
import ResidencyToggle from "@/components/ResidencyToggle";
import Spinner from "@/components/Spinner";
import { track } from "@/lib/analytics";
import { FileEdit, Copy, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { id: "job_loss", label: "Parent job loss or reduced income" },
  { id: "medical",  label: "Major medical expenses" },
  { id: "divorce",  label: "Parent divorce or separation" },
  { id: "death",    label: "Death of a parent or supporter" },
  { id: "other",    label: "Other significant change" },
];

export default function AppealPage() {
  const [data, setData] = useState({ student_name: "", school_name: "", residency: "", circumstance_type: "job_loss", circumstance: "", details: "", requested_amount: "" });
  const [letter, setLetter] = useState(null);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { track.tool_opened("appeal"); }, []);

  const set = (k, v) => setData(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setLetter(null);
    try {
      const body = { ...data, requested_amount: data.requested_amount ? Number(data.requested_amount) : undefined };
      const res = await fetch(`${API}/appeal`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("failed");
      const out = await res.json();
      setLetter(out.letter);
      setDocs(out.suggested_documents || []);
      track.appeal_drafted();
    } catch {
      setErr("Couldn't draft a letter right now. Try again in a moment.");
    } finally { setBusy(false); }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(letter || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <ToolShell tool="Tool 07 · Appeal Letter Writer" title="Draft a specific, respectful appeal." subtitle="Aid offices can adjust your package when your circumstances change. A good letter states the facts, references documentation, and asks for a specific outcome. Nothing you enter here is saved.">
      <form onSubmit={submit} className="card grid sm:grid-cols-2 gap-5" data-testid="appeal-form">
        <Field label="Student name" value={data.student_name} onChange={v => set("student_name", v)} testid="appeal-student" />
        <div>
          <label className="block text-sm text-ink-muted mb-2">School name</label>
          <CollegeSelect
            value={data.school_name}
            onChange={(name) => set("school_name", name)}
            placeholder="Search any US college"
            inputClassName="input"
            testId="appeal-school"
          />
        </div>
        <div className="sm:col-span-2">
          <ResidencyToggle
            value={data.residency}
            onChange={(v) => set("residency", v)}
            testId="appeal-residency"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm text-ink-muted mb-2">Circumstance type</label>
          <select className="select-clean" value={data.circumstance_type} onChange={e => set("circumstance_type", e.target.value)} data-testid="appeal-type">
            {TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm text-ink-muted mb-2">Brief description (one sentence)</label>
          <input className="input" value={data.circumstance} onChange={e => set("circumstance", e.target.value)} placeholder="e.g., My father was laid off in September." data-testid="appeal-circumstance" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm text-ink-muted mb-2">Fuller details (what changed, when, what's attached)</label>
          <textarea rows={5} className="input" value={data.details} onChange={e => set("details", e.target.value)} placeholder="Explain what happened, when, and what documentation you'll attach." data-testid="appeal-details" />
        </div>
        <div>
          <CurrencyInput
            id="appeal-amount"
            label="Adjustment requested (optional)"
            value={data.requested_amount}
            onChange={(v) => set("requested_amount", v)}
            placeholder="5,000"
            testid="appeal-amount"
          />
        </div>
        <div className="sm:col-span-2">
          <button type="submit" disabled={busy || !data.student_name || !data.school_name || !data.residency || !data.details} className="btn btn-primary" data-testid="appeal-submit">
            <FileEdit className="w-4 h-4" /> {busy ? "Drafting…" : "Draft my letter"}
          </button>
          {!data.residency && data.school_name && (
            <p className="text-xs text-amber mt-2" data-testid="appeal-residency-required">
              Pick In-State or Out-of-State for {data.school_name}.
            </p>
          )}
          {err && <p className="text-sm text-danger mt-3" data-testid="appeal-err">{err}</p>}
        </div>
      </form>

      {busy && !letter && (
        <Spinner variant="block" label="Drafting an appeal that fits your situation." testId="appeal-spinner" />
      )}

      {letter && (
        <div className="mt-8 grid md:grid-cols-3 gap-5" data-testid="appeal-result">
          <div className="md:col-span-2 card">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-ink-dim">Your draft</div>
              <button onClick={copy} className="btn btn-ghost text-xs" data-testid="appeal-copy">
                {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
              </button>
            </div>
            <pre className="whitespace-pre-wrap font-body text-sm text-ink leading-relaxed">{letter}</pre>
          </div>
          <div className="card">
            <div className="text-xs uppercase tracking-widest text-ink-dim mb-3">Attach these</div>
            <ul className="text-sm text-ink-muted space-y-2 list-disc pl-5">
              {docs.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
          </div>
        </div>
      )}
    </ToolShell>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-sm text-ink-muted mb-2">{label}</label>
      <input className="input" value={value} onChange={e => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}
